import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Header, Modal } from "@amzn/awsui-components-react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import { InvestigationPopulationBuilder } from "./InvestigationPopulationBuilder";
import {
  investigationPath,
  isTerminal,
  ownerLabel,
  ruleToSeed,
} from "./investigationUtils";

interface InvestigationSplitActionProps {
  stage: Stage;
  investigation: Investigation;
  currentUser?: string | null;
  /** Called after the child is created, before navigating to it. */
  onSplit?: (childId: string, movedCount: number) => void;
  /**
   * Controlled mode (opened from a menu): when set, no button is rendered and the builder is
   * shown while `open` is true; `onClose` is called when it is dismissed or done.
   */
  open?: boolean;
  onClose?: () => void;
}

/** Why Split is unavailable to this user, or undefined when it is available. */
export const splitBlocker = (
  investigation: Investigation,
  currentUser?: string | null
): string | undefined => {
  if (isTerminal(investigation.status) || !(investigation.rule || []).length)
    return "This investigation cannot be split.";
  if (!currentUser || investigation.owner !== currentUser)
    return `Only ${ownerLabel(
      investigation.owner
    )} can split this investigation.`;
  return undefined;
};

/** Why Split by list is unavailable to this user, or undefined when it is available. */
export const splitListBlocker = (
  investigation: Investigation,
  currentUser?: string | null
): string | undefined => {
  if (isTerminal(investigation.status))
    return "This investigation cannot be split.";
  if (!currentUser || investigation.owner !== currentUser)
    return `Only ${ownerLabel(
      investigation.owner
    )} can split this investigation.`;
  if ((investigation.assignedCount ?? 0) < 2)
    return "There are too few items to split.";
  return undefined;
};

/**
 * "Split" on an investigation page: opens the population builder pre-seeded with this
 * investigation's rule so the owner can pin one more field and carve a narrower child off it.
 * The child takes primary ownership of its subset (`mode=child`); this investigation keeps the
 * rest and both stay linked. Owner-only, like every other change to the population; hidden once
 * the investigation is resolved because the backend refuses children of resolved parents.
 */
export const InvestigationSplitAction: React.FC<
  InvestigationSplitActionProps
> = ({
  stage,
  investigation,
  currentUser,
  onSplit,
  open: controlledOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [ownOpen, setOwnOpen] = useState(false);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : ownOpen;
  const setOpen = (next: boolean) => {
    if (controlled) {
      if (!next) onClose?.();
    } else setOwnOpen(next);
  };
  const rule = useMemo(() => investigation.rule || [], [investigation.rule]);
  const seed = useMemo(() => ruleToSeed(rule), [rule]);
  const isOwner = !!currentUser && investigation.owner === currentUser;
  if (isTerminal(investigation.status) || rule.length === 0) return null;

  return (
    <>
      {!controlled && (
        <Button
          iconName="treeview-expand"
          disabled={!isOwner}
          disabledReason={
            isOwner
              ? undefined
              : `Only ${ownerLabel(
                  investigation.owner
                )} can split this investigation.`
          }
          onClick={() => setOpen(true)}
        >
          Split
        </Button>
      )}
      {open && (
        <Modal
          visible
          size="large"
          onDismiss={() => setOpen(false)}
          header={
            <Header
              variant="h2"
              description="Split a narrower case out of this investigation. It takes over the items that match the narrower rule; this investigation keeps the rest. Each case is resolved on its own."
            >
              Split {investigation.title}
            </Header>
          }
        >
          <InvestigationPopulationBuilder
            stage={stage}
            seed={seed}
            splitFrom={{
              investigationId: investigation.id,
              title: investigation.title,
              rule,
            }}
            onCreated={(childId, summary) => {
              setOpen(false);
              onSplit?.(childId, summary.assignedCount);
              navigate(investigationPath(childId, stage));
            }}
            onCancel={() => setOpen(false)}
          />
        </Modal>
      )}
    </>
  );
};

export default InvestigationSplitAction;
